/**
 * fuzzyNameMatch — Additive, seiteneffektfreie Helfer für UNSCHARFES
 * Firmen-Namen-Matching ("gleiche Firma, aber Name nicht 100% identisch").
 *
 * WICHTIG (rein additiv / bruchsicher):
 *   - Dieses Modul verändert NICHTS am bestehenden E-Mail-/Namens-/Domain-Dedup.
 *     Es berechnet nur zwei neue, optionale Felder:
 *       • business_name_strong   — stark normalisierter Name (Rechtsformen weg,
 *                                  Umlaute entfaltet, Tokens sortiert)
 *       • business_name_phonetic — Kölner Phonetik des starken Namens
 *     plus eine Ähnlichkeits-Funktion (Jaro-Winkler) zum Ranking/Bestätigen.
 *   - Die Logik ist bewusst identisch zur Server-Variante
 *     `functions/helpers/fuzzyNameMatch.js`, damit Client und Server
 *     bit-identisch normalisieren (sonst entstehen selbst Duplikate).
 *
 * Diese Felder dienen NUR der zusätzlichen "mögliche ähnliche Treffer"-Anzeige,
 * die der User manuell bestätigt. Es wird nichts automatisch übersprungen oder
 * gemerged.
 */

// Rechtsformen / generische Verbinder, die für die Firmen-Identität irrelevant
// sind. Bewusst KONSERVATIV gehalten: nur klare Rechtsformen + Bindewörter,
// keine Branchenwörter (sonst würden verschiedene Firmen fälschlich kollabieren).
const STRONG_STOPWORDS = new Set([
  // DE/AT/CH Rechtsformen
  'gmbh', 'mbh', 'ggmbh', 'gesmbh', 'ug', 'ag', 'kg', 'kgaa', 'ohg', 'gbr',
  'eg', 'ev', 'ek', 'og', 'se', 'haftungsbeschraenkt', 'co', 'cokg',
  // International
  'ltd', 'ltda', 'limited', 'llc', 'llp', 'lp', 'inc', 'incorporated',
  'corp', 'corporation', 'plc', 'sa', 'sas', 'sarl', 'srl', 'spa', 'bv',
  'nv', 'oy', 'ab', 'as', 'aps', 'pty', 'kft', 'sro', 'doo',
  // Bindewörter / Artikel
  'und', 'and', 'the', 'der', 'die', 'das', 'le', 'la', 'el',
]);

/**
 * Entfaltet Umlaute/Akzente in ASCII (ä→ae, ö→oe, ü→ue, ß→ss, é→e, …).
 */
const transliterate = (input) => {
  if (!input || typeof input !== 'string') return '';
  return input
    .replace(/ä/g, 'ae').replace(/Ä/g, 'ae')
    .replace(/ö/g, 'oe').replace(/Ö/g, 'oe')
    .replace(/ü/g, 'ue').replace(/Ü/g, 'ue')
    .replace(/ß/g, 'ss')
    // Generische Akzent-Entfernung (é, è, ñ, ç, …)
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
};

/**
 * Stark normalisierter Firmenname:
 *   - Umlaute/Akzente entfaltet, lowercase
 *   - nur Buchstaben/Ziffern/Leerzeichen
 *   - Rechtsformen + Bindewörter entfernt
 *   - Tokens alphabetisch sortiert (⇒ Wortreihenfolge egal:
 *     "Acme Marketing" == "Marketing Acme")
 *
 * Fällt auf die einfache Normalisierung zurück, falls durch das Entfernen der
 * Stopwords nichts übrig bliebe (damit "GmbH" allein nicht zu '' wird).
 */
const normalizeBusinessNameStrong = (name) => {
  if (!name || typeof name !== 'string') return '';

  const base = transliterate(name)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!base) return '';

  const tokens = base.split(' ').filter(Boolean);
  const meaningful = tokens.filter((t) => !STRONG_STOPWORDS.has(t));

  const finalTokens = meaningful.length > 0 ? meaningful : tokens;
  return finalTokens.slice().sort().join(' ');
};

/**
 * Kölner Phonetik (Cologne phonetics) — wie Soundex, aber für deutsche Namen
 * geeignet. Liefert einen Ziffern-Code, bei dem ähnlich klingende Namen
 * denselben Code erhalten (Maier/Mayer/Meyer → gleicher Code).
 *
 * Erwartet einen bereits transliterierten/normalisierten Buchstaben-String.
 */
const colognePhonetic = (input) => {
  if (!input || typeof input !== 'string') return '';
  const word = input.toLowerCase().replace(/[^a-z]/g, '');
  if (!word) return '';

  const codes = [];
  const len = word.length;

  for (let i = 0; i < len; i++) {
    const c = word[i];
    const prev = i > 0 ? word[i - 1] : '';
    const next = i < len - 1 ? word[i + 1] : '';
    let code = null;

    switch (c) {
      case 'a': case 'e': case 'i': case 'j': case 'o': case 'u': case 'y':
        code = '0';
        break;
      case 'b':
        code = '1';
        break;
      case 'p':
        code = next === 'h' ? '3' : '1';
        break;
      case 'd': case 't':
        code = 'csz'.includes(next) ? '8' : '2';
        break;
      case 'f': case 'v': case 'w':
        code = '3';
        break;
      case 'g': case 'k': case 'q':
        code = '4';
        break;
      case 'c':
        if (i === 0) {
          code = 'ahkloqrux'.includes(next) ? '4' : '8';
        } else if ('sz'.includes(prev)) {
          code = '8';
        } else {
          code = 'ahkoqux'.includes(next) ? '4' : '8';
        }
        break;
      case 'x':
        code = 'ckq'.includes(prev) ? '8' : '48';
        break;
      case 'l':
        code = '5';
        break;
      case 'm': case 'n':
        code = '6';
        break;
      case 'r':
        code = '7';
        break;
      case 's': case 'z':
        code = '8';
        break;
      case 'h':
      default:
        code = null; // H und Unbekanntes erzeugen keinen Code
        break;
    }

    if (code !== null) codes.push(code);
  }

  // Zu einem String zusammenführen (X kann "48" liefern).
  const raw = codes.join('');

  // Aufeinanderfolgende gleiche Ziffern zusammenfassen.
  let collapsed = '';
  for (let i = 0; i < raw.length; i++) {
    if (raw[i] !== raw[i - 1]) collapsed += raw[i];
  }

  // Alle '0' entfernen außer einer führenden.
  const first = collapsed[0] || '';
  const rest = collapsed.slice(1).replace(/0/g, '');
  return first + rest;
};

/**
 * Phonetischer Schlüssel eines Firmennamens (Kölner Phonetik des starken,
 * sortierten Namens). Gibt '' zurück, wenn kein sinnvoller Schlüssel ableitbar.
 */
const computeNamePhonetic = (name) => {
  const strong = normalizeBusinessNameStrong(name);
  if (!strong) return '';
  // Leerzeichen entfernen, damit Tokens als ein Klangbild kodiert werden.
  return colognePhonetic(strong.replace(/\s+/g, ''));
};

/**
 * Jaro-Winkler-Ähnlichkeit (0..1). Robust gegen Tippfehler und gut für
 * kurze-bis-mittlere Strings wie Firmennamen.
 */
const jaroWinkler = (a, b) => {
  const s1 = a || '';
  const s2 = b || '';
  if (s1 === s2) return 1;
  if (!s1.length || !s2.length) return 0;

  const matchWindow = Math.max(0, Math.floor(Math.max(s1.length, s2.length) / 2) - 1);
  const s1Matches = new Array(s1.length).fill(false);
  const s2Matches = new Array(s2.length).fill(false);

  let matches = 0;
  for (let i = 0; i < s1.length; i++) {
    const start = Math.max(0, i - matchWindow);
    const end = Math.min(i + matchWindow + 1, s2.length);
    for (let j = start; j < end; j++) {
      if (s2Matches[j] || s1[i] !== s2[j]) continue;
      s1Matches[i] = true;
      s2Matches[j] = true;
      matches++;
      break;
    }
  }

  if (matches === 0) return 0;

  let transpositions = 0;
  let k = 0;
  for (let i = 0; i < s1.length; i++) {
    if (!s1Matches[i]) continue;
    while (!s2Matches[k]) k++;
    if (s1[i] !== s2[k]) transpositions++;
    k++;
  }
  transpositions /= 2;

  const m = matches;
  const jaro = (m / s1.length + m / s2.length + (m - transpositions) / m) / 3;

  // Winkler-Bonus für gemeinsames Präfix (max. 4 Zeichen).
  let prefix = 0;
  const maxPrefix = Math.min(4, s1.length, s2.length);
  for (let i = 0; i < maxPrefix; i++) {
    if (s1[i] === s2[i]) prefix++;
    else break;
  }

  return jaro + prefix * 0.1 * (1 - jaro);
};

/**
 * Ähnlichkeit zweier Firmennamen (0..1) auf Basis ihrer starken Normalisierung.
 */
const nameSimilarity = (nameA, nameB) => {
  const a = normalizeBusinessNameStrong(nameA);
  const b = normalizeBusinessNameStrong(nameB);
  if (!a || !b) return 0;
  return jaroWinkler(a, b);
};

export {
  STRONG_STOPWORDS,
  transliterate,
  normalizeBusinessNameStrong,
  colognePhonetic,
  computeNamePhonetic,
  jaroWinkler,
  nameSimilarity,
};
